// Components
import { Box, Grid, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useMemo } from "react";

import { useToggleModal } from "@customer-app/state/actionCreators/modal";
import { useSellingPlanGroupForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { ISubscriptionProps } from "./constants";
import { FutureOrderModule } from "./V1SubscriptionManagement/libs/components/FutureOrderModule";
import { AddonListCard } from "./V2SubscriptionManagement/libs/components/AddonListCard";
import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";
import { getGift, useOrganization } from "../../../utils";
import { usePurchasablesForAdding } from "../../hooks/usePurchasablesForAdding";
import { useUnavailableVariantsAndDeliveries } from "../../hooks/useUnavailableVariantsAndDeliveries";
import { useBillStore } from "../../store/BillsStore";
import { useSubscriptionActionStates } from "../../store/SubscriptionActionStore";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const YourNextOrderAndAddons = ({ upcomingDelivery }: ISubscriptionProps): JSX.Element => {
  const organization = useOrganization();
  const theme = useSmartrrTheme();
  const { failedBills } = useBillStore();
  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();

  const { purchaseState } = upcomingDelivery!;

  const purchasablesForAdding = usePurchasablesForAdding(upcomingDelivery);

  const skdIdx = upcomingDelivery?.indexFromScheduleStart;

  const gift = getGift(purchaseState, skdIdx)!;

  const { isLoading } = useSubscriptionActionStates();

  const isSellingPlanDeleted = !useSellingPlanGroupForSellingPlanId(purchaseState.sellingPlanId).length;
  const isPurchaseStatesInitialized = useSmartrrCustomerSelector(state => state.purchaseStates.isInitialized);
  const isSellingPlanGroupsLoading = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlansIsLoading
  );
  const isError = isSellingPlanDeleted && isPurchaseStatesInitialized && !isSellingPlanGroupsLoading;

  const isPrepaid = isCPSPrepaid(purchaseState.schedule);

  const delayDateAvailable =
    upcomingDelivery?.date.toDateString() ===
    getNextActualDeliveryForCpsGroup(organization!, [purchaseState])?.[0].toDateString();

  const containsOOSItems = unavailableVariantsAndDeliveries.vnts.some(oosItem =>
    upcomingDelivery?.purchaseState.stLineItems.some(lineItem => oosItem.id === lineItem.vnt.id)
  );

  const cpsHasFailedBills = useMemo(() => !!failedBills.length, [failedBills.length]);

  const showImportantNoticeBadge = useMemo(
    () => isError || containsOOSItems || cpsHasFailedBills,
    [isError, containsOOSItems, cpsHasFailedBills]
  );

  const toggleModal = useToggleModal();

  return (
    <Grid container spacing={"20px"} className={prefixClassName("your-next-order-addons")}>
      <Grid item xs={12} md={6}>
        <AquariusCard className={prefixClassName("next-order-card")} sx={{ height: "100%" }}>
          <Stack direction="row" alignItems="center" sx={{ marginBottom: "15px" }} spacing="7px">
            <Typography variant="h4" style={{ marginBottom: "0px" }}>
              {typedLocalize("subscriptions.yourNextOrder.title")}
            </Typography>
            {showImportantNoticeBadge ? (
              <AquariusBadge
                icon="exclamationMark"
                label={typedLocalize("badgeStatus.importantNotice")}
                sx={{ marginBottom: "7px" }}
              />
            ) : null}
            {gift ? (
              <AquariusBadge icon="gift" label={typedLocalize("subscriptions.yourNextOrder.giftedLabel")} />
            ) : null}
          </Stack>
          <Typography variant="h3" className={prefixClassName("next-order-date")}>
            {cpsHasFailedBills
              ? typedLocalize("subscriptions.yourNextOrder.failedBillsNotice")
              : typedLocalize("utils.dates.toViewDateLongDay", {
                  date: upcomingDelivery?.date,
                })}
          </Typography>
          {upcomingDelivery ? (
            <div className={prefixClassName("upcoming-deliveries")}>
              <FutureOrderModule delivery={upcomingDelivery} />
            </div>
          ) : null}

          <Box
            sx={{
              "& button": {
                marginTop: "0px !important",
              },
            }}
          >
            <AquariusButton
              filled
              disabled={isLoading}
              onClick={() =>
                toggleModal("ORDER_OPTIONS", {
                  deliveryId: getDeliveryId(upcomingDelivery),
                  delayDateAvailable,
                  gift,
                  isSellingPlanDeleted: isError,
                  purchasablesForAdding,
                  organization,
                })
              }
              sx={{ marginTop: "0px" }}
            >
              {localize(`subscriptions.yourNextOrder.${isError ? "viewImportantNotice" : "manageNextOrder"}`)}
            </AquariusButton>
          </Box>
          {theme.modernThemeSettings?.showHide.showUpcomingOrders ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
                marginTop: "15px",
              }}
            >
              <Typography
                variant="caption1"
                className={prefixClassName("upcoming-orders-link")}
                onClick={() =>
                  toggleModal("UPCOMING_ORDERS", {
                    delivery: upcomingDelivery,
                  })
                }
                sx={{
                  "&": {
                    pointerEvents: isLoading ? "none" : "all",
                  },
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }}
              >
                {typedLocalize("subscriptions.yourNextOrder.seeUpcoming")}
              </Typography>
            </Box>
          ) : null}
        </AquariusCard>
      </Grid>

      {purchasablesForAdding.length &&
      theme.modernThemeSettings?.sections.subscriptions.addOns &&
      !isPrepaid &&
      !gift ? (
        <Grid item xs={12} md={6}>
          <AddonListCard purchasables={purchasablesForAdding} delivery={upcomingDelivery} />
        </Grid>
      ) : null}
    </Grid>
  );
};
